import type { DocumentContent, NumberedSection, RichNode, Section } from './types.js';

export interface NumberingOptions {
  /** Include unnumbered sections in the flattened output (default true). */
  includeUnnumbered?: boolean;
  /** Skip sections flagged `internal` (default false — exports set this true). */
  skipInternal?: boolean;
}

/**
 * Flattens the section tree, assigning hierarchical clause numbers.
 *
 * Unnumbered sections (e.g. "Document control") do not consume a number and do
 * not reset the counter for their siblings.
 */
export function flattenSections(content: DocumentContent, options: NumberingOptions = {}): NumberedSection[] {
  const { includeUnnumbered = true, skipInternal = false } = options;
  const out: NumberedSection[] = [];

  const recurse = (sections: Section[], depth: number, prefix: string, path: string[]) => {
    let counter = 0;
    for (const section of sections) {
      if (skipInternal && section.internal) continue;
      let number = '';
      if (section.numbered) {
        counter += 1;
        number = prefix ? `${prefix}.${counter}` : String(counter);
      }
      if (section.numbered || includeUnnumbered) {
        out.push({ section, number, depth, path: [...path, section.title] });
      }
      recurse(section.children, depth + 1, number || prefix, [...path, section.title]);
    }
  };

  recurse(content.sections, 1, '', []);
  return out;
}

/** Table-of-contents entries for exports and the editor outline. */
export interface TocEntry {
  id: string;
  anchor: string;
  number: string;
  title: string;
  depth: number;
}

export function buildToc(content: DocumentContent, options: NumberingOptions = {}): TocEntry[] {
  return flattenSections(content, options).map(({ section, number, depth }) => ({
    id: section.id,
    anchor: section.anchor,
    number,
    title: section.title,
    depth,
  }));
}

/** Resolves the clause number for a section anchor, if it has one. */
export function numberForAnchor(content: DocumentContent, anchor: string): string | null {
  const match = flattenSections(content).find((entry) => entry.section.anchor === anchor);
  return match?.number || null;
}

/** Walks every inline node in a rich body. */
export function walkRichNodes(node: RichNode | undefined, visit: (node: RichNode) => void): void {
  if (!node) return;
  visit(node);
  for (const child of node.content ?? []) walkRichNodes(child, visit);
}
